import { fetchWithAuth } from './core'
import type { ChatImage } from '@/components/layout/chat-pane.types'

function extForMediaType(mediaType: ChatImage['mediaType']): string {
  if (mediaType === 'image/png') return 'png'
  if (mediaType === 'image/gif') return 'gif'
  if (mediaType === 'image/webp') return 'webp'
  return 'jpg'
}

/** Send blocks on staging; a stalled Storage upload must fall back to base64 quickly. */
const CHAT_STAGING_UPLOAD_TIMEOUT_MS = 15_000

/** Upload a composer image to chat staging. Returns the same image plus attachmentId. */
export async function uploadChatStagingImage(
  img: ChatImage,
  opts?: { threadId?: string | null; conversationId?: string | null; organizationId?: string | null },
): Promise<ChatImage> {
  if (img.attachmentId) return img

  const res = await fetch(img.dataUrl)
  const blob = await res.blob()
  const form = new FormData()
  form.append('file', blob, `chat-image.${extForMediaType(img.mediaType)}`)
  if (opts?.threadId) form.append('threadId', opts.threadId)
  if (opts?.conversationId) form.append('conversationId', opts.conversationId)
  if (opts?.organizationId) form.append('organizationId', opts.organizationId)

  const response = await fetchWithAuth('/api/attachments/upload-chat', {
    method: 'POST',
    body: form,
    signal: AbortSignal.timeout(CHAT_STAGING_UPLOAD_TIMEOUT_MS),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.details || err.error || 'Chat image upload failed')
  }
  const result = await response.json()
  const attachmentId = result?.attachment?.id
  if (!attachmentId) throw new Error('Chat image upload returned no id')
  return { ...img, attachmentId: String(attachmentId) }
}

/**
 * Stage composer images in the selected workspace.
 * No workspace id: do not upload. A missing id used to land on the account
 * primary org (Hop). The resized local image is sent for this turn only.
 */
export async function stageChatImagesForSend(
  images: ChatImage[],
  opts?: { threadId?: string | null; conversationId?: string | null; organizationId?: string | null },
): Promise<ChatImage[]> {
  if (!opts?.organizationId) {
    console.warn('[chat] skipping image upload; no workspace id')
    return images
  }
  const out: ChatImage[] = []
  for (const img of images) {
    try {
      out.push(await uploadChatStagingImage(img, opts))
    } catch (err) {
      console.warn('[chat] staging upload failed; last-turn base64 fallback', err)
      out.push(img)
    }
  }
  return out
}
